import { useState, useRef, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useMedicalCodeSearch } from '@/hooks/use-medical-codes';
import type { MedicalCodeType } from '@vagusx/clients';

/**
 * Autocomplete input for medical codes (ICD-10-CM, CPT).
 * Shows a dropdown of matching codes as the user types.
 * Selected codes appear as removable tags with description labels.
 */
export function CodeAutocomplete({
  codeType,
  tags,
  onChange,
  placeholder,
}: {
  codeType: MedicalCodeType;
  tags: string[];
  onChange: (tags: string[]) => void;
  placeholder?: string;
}): React.ReactElement {
  const [input, setInput] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cache code → description for display in tags
  const [labelCache, setLabelCache] = useState<Record<string, string>>({});

  const { data: results = [], isLoading } = useMedicalCodeSearch(codeType, input);

  // Filter out already-selected codes
  const filtered = results.filter((r) => !tags.includes(r.code));

  const addCode = useCallback(
    (code: string, description?: string) => {
      if (!tags.includes(code)) {
        onChange([...tags, code]);
        if (description) {
          setLabelCache((prev) => ({ ...prev, [code]: description }));
        }
      }
      setInput('');
      setOpen(false);
      setHighlightIndex(0);
      inputRef.current?.focus();
    },
    [tags, onChange]
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered.length > 0 && open) {
        const item = filtered[highlightIndex] ?? filtered[0]!;
        addCode(item.code, item.description);
      } else if (input.trim()) {
        // Allow free-text entry if no results
        addCode(input.trim().toUpperCase());
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else if (e.key === 'Backspace' && !input && tags.length > 0) {
      onChange(tags.slice(0, -1));
    }
  };

  /** Truncate description to ~30 chars for the pill */
  const shortLabel = (code: string): string | null => {
    const desc = labelCache[code];
    if (!desc) return null;
    return desc.length > 35 ? desc.slice(0, 32) + '...' : desc;
  };

  return (
    <div className="relative">
      <div className="flex flex-wrap gap-1.5 items-center rounded-md border px-2 py-1.5 focus-within:ring-1 focus-within:ring-ring">
        {tags.map((tag) => (
          <Badge key={tag} variant="secondary" className="text-xs gap-1.5 shrink-0 max-w-full">
            <span className="font-mono font-medium">{tag}</span>
            {shortLabel(tag) && (
              <span className="text-muted-foreground truncate">{shortLabel(tag)}</span>
            )}
            <button
              type="button"
              onClick={() => onChange(tags.filter((t) => t !== tag))}
              className="ml-0.5 shrink-0 hover:text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <div className="relative flex-1 min-w-[6rem]">
          <input
            ref={inputRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setOpen(e.target.value.trim().length >= 2);
              setHighlightIndex(0);
            }}
            onFocus={() => {
              if (input.trim().length >= 2) setOpen(true);
            }}
            onBlur={() => {
              // Delay close to allow click on dropdown items
              setTimeout(() => setOpen(false), 200);
            }}
            onKeyDown={handleKeyDown}
            placeholder={tags.length === 0 ? placeholder : ''}
            className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground h-6"
          />
          {isLoading && input.trim().length >= 2 && (
            <Loader2 className="absolute right-0 top-0.5 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
      </div>

      {/* Dropdown */}
      {open && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md max-h-60 overflow-auto">
          {filtered.map((result, idx) => (
            <button
              key={result.code}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault(); // Prevent blur
                addCode(result.code, result.description);
              }}
              onMouseEnter={() => setHighlightIndex(idx)}
              className={`w-full flex items-start gap-2 px-3 py-2 text-left text-sm transition-colors ${
                idx === highlightIndex ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50'
              }`}
            >
              <span className="font-mono font-medium shrink-0 text-xs mt-0.5 min-w-[4rem]">
                {result.code}
              </span>
              <span className="text-muted-foreground text-xs line-clamp-2">
                {result.description}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* No results state */}
      {open && !isLoading && input.trim().length >= 2 && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-md px-3 py-2">
          <span className="text-sm text-muted-foreground">
            No codes found. Press Enter to add &ldquo;{input.trim().toUpperCase()}&rdquo; manually.
          </span>
        </div>
      )}
    </div>
  );
}
